import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MenuItem, Select, Box, Typography, CircularProgress } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import * as CountryFlags from "country-flag-icons/react/3x2";
import { useGetAllPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { setEcrOpsSelectedPlatform } from "state/slices/ecrOpsPlatformSlice";

const STORAGE_KEY = "ecrOpsSelectedPlatform";

// Renders the flag matching the platform's API-provided `flag_code`
// (e.g. "US", "EU", "GB"). Unknown or missing codes fall back to a globe icon,
// so adding a new platform never requires a frontend change.
const PlatformIcon = ({ flagCode }) => {
  const Flag = flagCode ? CountryFlags[String(flagCode).toUpperCase()] : null;

  if (!Flag) {
    return <PublicIcon sx={{ fontSize: 20, color: "#008080" }} />;
  }

  return <Flag style={{ width: 24, height: 16, borderRadius: "2px" }} />;
};

// Platform picker scoped to the ECR Ops Portal — mirrors the Home Dashboard's
// PlatformSelector but reads/writes ecrOpsPlatformSlice, so a selection made
// here never affects (or is affected by) the Home Dashboard's own selection.
const EcrOpsPlatformSelector = ({ disabled = false }) => {
  const dispatch = useDispatch();
  const { data: platforms, isLoading } = useGetAllPlatformsQuery(true);
  const selectedPlatform = useSelector((state) => state.ecrOpsPlatform);

  const filteredPlatforms = platforms || [];

  const defaultPlatform = filteredPlatforms?.find((p) => p.name === "deloitte-global");

  useEffect(() => {
    if (filteredPlatforms.length > 0 && !selectedPlatform.selectedPlatformId) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const savedPlatform = filteredPlatforms.find((p) => p._id === parsed.platformId);
          if (savedPlatform) {
            dispatch(
              setEcrOpsSelectedPlatform({
                platformId: savedPlatform._id,
                platformName: savedPlatform.display_name,
              })
            );
            return;
          }
        } catch (e) {
          // ignore JSON parse errors and fall back to default
        }
      }

      if (defaultPlatform) {
        dispatch(
          setEcrOpsSelectedPlatform({
            platformId: defaultPlatform._id,
            platformName: defaultPlatform.display_name,
          })
        );
        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              platformId: defaultPlatform._id,
              platformName: defaultPlatform.display_name,
            })
          );
        } catch (e) {
          // ignore storage errors
        }
      }
    }
  }, [filteredPlatforms, defaultPlatform, selectedPlatform.selectedPlatformId, dispatch]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" sx={{ color: "#666", whiteSpace: "nowrap", fontWeight: "500" }}>
        Platform
      </Typography>
      <Select
        value={selectedPlatform.selectedPlatformId || defaultPlatform?._id || ""}
        onChange={(e) => {
          const platformId = e.target.value;
          const platform = filteredPlatforms?.find((p) => p._id === platformId);
          if (platform) {
            dispatch(
              setEcrOpsSelectedPlatform({
                platformId: platform._id,
                platformName: platform.display_name,
              })
            );
            try {
              localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                  platformId: platform._id,
                  platformName: platform.display_name,
                })
              );
            } catch (e) {
              // ignore storage errors
            }
          }
        }}
        renderValue={(value) => {
          const platform = filteredPlatforms?.find((p) => p._id === value);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <PlatformIcon flagCode={platform?.flag_code} />
              <span>{platform?.display_name || "Select Platform"}</span>
            </Box>
          );
        }}
        displayEmpty
        size="small"
        disabled={disabled || isLoading}
        sx={{
          minWidth: 200,
          height: "31px",
          "& .MuiSelect-select": {
            height: "31px",
            display: "flex",
            alignItems: "center",
            padding: "0 14px",
          },
        }}
      >
        {isLoading ? (
          <MenuItem value="" disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Loading platforms...
          </MenuItem>
        ) : filteredPlatforms && filteredPlatforms.length > 0 ? (
          filteredPlatforms.map((platform) => (
            <MenuItem key={platform._id} value={platform._id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <PlatformIcon flagCode={platform.flag_code} />
                <span>{platform.display_name}</span>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem value="" disabled>
            No platforms available
          </MenuItem>
        )}
      </Select>
    </Box>
  );
};

export default EcrOpsPlatformSelector;
